import type { TechStackConfig } from '~/lib/tech-spec/types';
import { STACK_CATEGORIES } from '~/lib/tech-spec/types';
import StackPresetPicker from './StackPresetPicker';

interface Props {
  value: TechStackConfig;
  onChange: (next: TechStackConfig) => void;
}

export default function StackSelector({ value, onChange }: Props) {
  function set<K extends keyof TechStackConfig>(
    key: K,
    val: TechStackConfig[K],
  ) {
    onChange({ ...value, [key]: val });
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Presets
        </p>
        <StackPresetPicker current={value} onChange={onChange} />
      </div>

      {STACK_CATEGORIES.map((cat) => (
        <div key={cat.id}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {cat.label}
          </p>
          <div className="flex flex-col gap-1">
            {cat.options.map((opt) => {
              const selected = value[cat.id] === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() =>
                    set(cat.id, opt.id as TechStackConfig[typeof cat.id])
                  }
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                    selected
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                      : 'bg-white border-gray-100 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span
                    className={`mt-0.5 h-3.5 w-3.5 rounded-full border-2 flex-shrink-0 ${
                      selected
                        ? 'border-indigo-600 bg-indigo-600'
                        : 'border-gray-300'
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="text-sm font-medium block">
                      {opt.label}
                    </span>
                    <span className="text-xs text-gray-400 block">
                      {opt.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
